import { AsyncPipe, NgClass, NgIf } from '@angular/common';
import { Component, computed, OnInit, Signal, signal, TemplateRef, ViewChild, WritableSignal } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButton, MatIconButton } from '@angular/material/button';
import { MatButtonToggle, MatButtonToggleGroup } from '@angular/material/button-toggle';
import {
  MatCard,
  MatCardActions,
  MatCardContent,
  MatCardHeader,
  MatCardSubtitle,
  MatCardTitle,
} from '@angular/material/card';
import { MatCheckbox } from '@angular/material/checkbox';
import {
  MatDialogActions,
  MatDialogClose,
  MatDialogContent,
  MatDialogRef,
  MatDialogTitle,
} from '@angular/material/dialog';
import { MatExpansionPanel, MatExpansionPanelHeader, MatExpansionPanelTitle } from '@angular/material/expansion';
import { MatError, MatFormField, MatLabel, MatSuffix } from '@angular/material/form-field';
import { MatIcon } from '@angular/material/icon';
import { MatInput } from '@angular/material/input';
import { MatMenu, MatMenuItem, MatMenuTrigger } from '@angular/material/menu';
import { MatTooltip } from '@angular/material/tooltip';
import { ActivatedRoute, ParamMap, Params, Router, RouterLink } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import { cloneDeep, get } from 'lodash';
import { combineLatest, debounceTime, distinctUntilChanged, Observable, Subscription } from 'rxjs';
import { filter, map, take } from 'rxjs/operators';
import {
  CARD_SORT_MENU_OPTIONS,
  CARD_SORT_REQUEST_SORTS,
  ECardSortOption,
  ICardSortMenuOption,
} from '../../../core/constants/card-sort.constants';
import { EDialogWidth } from '../../../core/constants/ui.constants';
import { DashboardRefreshIntervalService } from '../../../core/services/dashboard-refresh-interval.service';
import { UserPinService } from '../../../core/services/user-pin.service';
import { TrimmedRequiredValidator } from '../../../core/validators/trimmed-required.validator';
import { IBreadcrumbNode } from '../../../shared/components/breadcrumb/breadcrumb.component';
import {
  CloneDialogComponent,
  ICloneDialogConfig,
} from '../../../shared/components/clone-dialog/clone-dialog.component';
import { ConfirmationDialogComponent } from '../../../shared/components/confirmation-dialog/confirmation-dialog.component';
import { ErrorMessagesComponent } from '../../../shared/components/error-messages/error-messages.component';
import { HeaderBreadcrumbManager } from '../../../shared/components/header-breadcrumb/header-breadcrumb-manager';
import { IDropdownOption, MatDropdown } from '../../../shared/components/mat-dropdown/mat-dropdown.component';
import { IDropdownSettings } from '../../../shared/components/mat-dropdown/scw-mat-dropdown.interface';
import { ScreenLockConfigurationComponent } from '../../../shared/components/screen-lock-configuration/screen-lock-configuration.component';
import { ELoadStatus } from '../../../state/state.interface';
import { UserEffects } from '../../../state/user/user.effects';
import { ICurrentUser } from '../../../state/user/user.interface';
import { FormUtilitiesService } from '../../../utilities/form-utilities.service';
import {
  HttpUtilitiesService,
  IGenericCrudRequestConstructionParameterFilters,
} from '../../../utilities/http-utilities.service';
import { ESnackbar } from '../../../utilities/snackbar.service';
import { BaseCrudPageComponent } from '../../app/base-crud-page.component';
import { EDashboardLayout } from '../../dashboard-content/dashboard-content.interface';
import { SlideshowsComponent } from '../../slideshows/slideshows.component';
import {
  EDashboardViewMode,
  IDashboard,
  IDashboardCard,
  TAddDashboard,
  TDashboardChild,
  TFormControlDashboard,
  TFormDashboardComponent,
  TRefreshIntervalOptionsWithoutDashboardDefault,
} from './dashboards.interface';
import { DASHBOARDS_QUERY_PARAMS, DashboardsRoute } from './dashboards.paths';
import { DashboardsStore, IDashboardState } from './dashboards.store';

@Component({
  imports: [
    ErrorMessagesComponent,
    MatButton,
    MatCard,
    MatCheckbox,
    MatDialogActions,
    MatDialogClose,
    MatDialogContent,
    MatDialogTitle,
    MatError,
    MatExpansionPanel,
    MatExpansionPanelHeader,
    MatExpansionPanelTitle,
    MatFormField,
    MatInput,
    MatLabel,
    NgIf,
    ReactiveFormsModule,
    TranslateModule,
    MatDropdown,
    RouterLink,
    MatIcon,
    AsyncPipe,
    MatCardHeader,
    MatCardActions,
    MatCardTitle,
    MatCardSubtitle,
    MatIconButton,
    MatMenu,
    MatMenuItem,
    MatMenuTrigger,
    MatButtonToggleGroup,
    MatButtonToggle,
    NgClass,
    MatTooltip,
    MatCardContent,
    MatSuffix,
    SlideshowsComponent,
    ScreenLockConfigurationComponent,
  ],
  providers: [DashboardsStore, HttpUtilitiesService],
  selector: 'app-dashboards',
  standalone: true,
  styleUrls: ['./dashboards.component.scss'],
  templateUrl: './dashboards.component.html',
})
export class DashboardsComponent extends BaseCrudPageComponent<IDashboard, IDashboardState> implements OnInit {
  @ViewChild('addEditFormDialog') addEditFormDialog!: TemplateRef<HTMLElement>;
  @ViewChild(SlideshowsComponent) slideshowsComponent?: SlideshowsComponent;

  public readonly refreshIntervalDropdownConfiguration: IDropdownSettings = {
    enableSearchFilter: false,
    hasMatError: true,
    isClientSide: true,
    repositionDropdownList: true,
    singleSelection: true,
    text: this.translate.instant('field.refreshInterval'),
  };

  public readonly workspaceDropdownConfig: IDropdownSettings = {
    hasDelayedDefaultValue: true,
    repositionDropdownList: true,
    singleSelection: true,
    text: this.translate.instant('field.workspace'),
  };

  public readonly parentDropdownSettings: IDropdownSettings = {
    enableSearchFilter: true,
    hasDelayedDefaultValue: true,
    isClientSide: false,
    repositionDropdownList: true,
    singleSelection: true,
    text: this.translate.instant('field.parentDashboard'),
  };

  private readonly defaultRefreshIntervalId: TRefreshIntervalOptionsWithoutDashboardDefault = 300;

  /** Same list the section renders; kept here so add/edit can map a stored duration back to a selection. */
  public readonly screenLockDurationOptions = this.dashboardRefreshIntervalService.autoLockDurationOptions;

  public readonly refreshIntervalData: IDropdownOption<TRefreshIntervalOptionsWithoutDashboardDefault>[] = cloneDeep(
    this.dashboardRefreshIntervalService.refreshDataIntervalOptions,
  ).filter(({ id }) =>
    [-1, 300, 900, 1800, 2700, 3600].includes(id),
  ) as IDropdownOption<TRefreshIntervalOptionsWithoutDashboardDefault>[];

  public readonly layoutOptions = Object.keys(EDashboardLayout)
    .filter((key) => isNaN(Number(key)))
    .map((key) => {
      return {
        id: EDashboardLayout[key as keyof typeof EDashboardLayout],
        name: this.translate.instant(`page.dashboards.layout.${key}`),
      };
    });
  private headerBreadcrumbManager!: HeaderBreadcrumbManager;
  private nodeClickedSubscription?: Subscription;
  public readonly dashboardsRoute = DashboardsRoute;
  public isToolAdmin: boolean = UserEffects.getUser().isAdmin;
  public isExtendedAccessPanel = false;
  public isExtendedDisplayConfigurationPanel = false;
  public isExtendedScreenLockPanel = false;
  public isAutoLockConfigured = false;
  public breadcrumbNodes: IBreadcrumbNode[] = [];
  public currentParentId: number | null = null;
  public search: string | undefined;

  constructor(
    public readonly store: DashboardsStore,
    public readonly formService: FormUtilitiesService,
    private readonly dashboardRefreshIntervalService: DashboardRefreshIntervalService,
    private readonly userPinService: UserPinService,
    private readonly route: ActivatedRoute,
    private readonly router: Router,
  ) {
    super(store, {
      ambiguousNumberContext: 'field.ambiguousNumberOfDashboards',
      multiContext: 'field.dashboards',
      nameProperty: 'name',
      singleContext: 'field.dashboard',
    });
  }

  /** Row filters for the list request: scope (root, pinned or a parent) plus the view mode. */
  public static buildListFilters(
    parentId: number | null,
    viewMode: EDashboardViewMode,
  ): IGenericCrudRequestConstructionParameterFilters[] {
    const filters: IGenericCrudRequestConstructionParameterFilters[] = [];

    if (viewMode === EDashboardViewMode.onlyVisible && parentId === null) {
      filters.push({ field: 'isRootOrPinned', ids: [true] });
    } else if (parentId !== null) {
      filters.push({ field: 'parentId', ids: [parentId] });
    }

    if (viewMode === EDashboardViewMode.onlyVisible) {
      filters.push({ field: 'isHidden', ids: [false] });
    } else if (viewMode === EDashboardViewMode.onlySubDashboards) {
      filters.push({ field: 'isHidden', ids: [true] });
    }

    return filters;
  }

  /** Private means: assigned to nobody but the current user, and to no team. */
  public static isPrivateDashboard(dashboard: IDashboard, currentUserId: number): boolean {
    return (
      dashboard?.dashboardTeamAssignments?.length === 0 &&
      dashboard?.dashboardUserAssignments?.length === 1 &&
      dashboard?.dashboardUserAssignments[0]?.userId === currentUserId
    );
  }

  /** Sub-dashboards are listed in their own view only — flat, and never in the dashboards list. */
  public static isListedInViewMode(viewMode: EDashboardViewMode, isHidden: boolean): boolean {
    if (viewMode === EDashboardViewMode.all) {
      return true;
    }

    return viewMode === EDashboardViewMode.onlySubDashboards ? isHidden : !isHidden;
  }

  /** The children page runs in the dashboards view, so an all-sub-dashboard parent would open empty. */
  public static hasVisibleChildren(children: TDashboardChild[] | undefined): boolean {
    return children?.some((child: TDashboardChild): boolean => !child.isHidden) ?? false;
  }

  public readonly formattedDatatableRows: Signal<IDashboardCard[]> = computed(() => {
    const viewMode: EDashboardViewMode = this.viewMode();

    return this.store
      .data$()
      .filter((row: IDashboard): boolean => DashboardsComponent.isListedInViewMode(viewMode, row.isHidden))
      .map((row: IDashboard): IDashboardCard => {
        return {
          hasVisibleChildren: DashboardsComponent.hasVisibleChildren(row.children),
          id: row.id,
          isAutoLockConfigured: row.isAutoLockConfigured,
          isCurrentUserAdmin: row.isCurrentUserAdmin,
          isHidden: row.isHidden,
          isPinned: row.isPinned,
          isPrivate: DashboardsComponent.isPrivateDashboard(row, UserEffects.getUser().id),
          name: row.name,
          parentId: row.parent?.id ?? null,
          refreshInterval: row.refreshInterval,
          workspace: row.workspace?.name,
        };
      });
  });

  public readonly addEditForm: FormGroup<TFormControlDashboard> = new FormGroup<TFormControlDashboard>({
    admins: new FormControl([] as IDropdownOption[], { validators: [Validators.required] }),
    autoLockDuration: new FormControl([] as IDropdownOption[]),
    autoLockPin: new FormControl<string | null>(null),
    isHidden: new FormControl(false, { nonNullable: true }),
    isPublished: new FormControl(false, { nonNullable: true }),
    isWidgetFilterButtonHidden: new FormControl(false, { nonNullable: true }),
    layoutMode: new FormControl([] as IDropdownOption[], { validators: [Validators.required] }),
    name: new FormControl('', [Validators.required, TrimmedRequiredValidator(), Validators.maxLength(50)]),
    parent: new FormControl([] as IDropdownOption[]),
    refreshInterval: new FormControl([] as IDropdownOption<TRefreshIntervalOptionsWithoutDashboardDefault>[], [
      Validators.required,
    ]),
    teams: new FormControl([] as IDropdownOption[]),
    users: new FormControl([] as IDropdownOption[]),
    workspace: new FormControl([] as IDropdownOption[], { nonNullable: true, validators: [Validators.required] }),
  });

  public readonly sortOptions: ICardSortMenuOption[] = CARD_SORT_MENU_OPTIONS;
  public readonly sortOption: WritableSignal<ECardSortOption> = signal(ECardSortOption.name);
  public readonly viewModeControl = new FormControl(EDashboardViewMode.onlyVisible, { nonNullable: true });
  /** Signal mirror of `viewModeControl.value`; update it through `syncViewModeState()` only. */
  public readonly viewMode: WritableSignal<EDashboardViewMode> = signal(EDashboardViewMode.onlyVisible);
  public readonly searchControl: FormControl<string> = new FormControl<string>('', { nonNullable: true });
  public readonly EDashboardViewMode = EDashboardViewMode;

  public readonly viewModeDropdownConfig: IDropdownSettings = {
    enableSearchFilter: false,
    hasMatError: false,
    isClientSide: true,
    repositionDropdownList: true,
    singleSelection: true,
    text: this.translate.instant('field.mode'),
  };

  public readonly viewModeOptions: IDropdownOption<EDashboardViewMode>[] = [
    { id: EDashboardViewMode.all, name: this.translate.instant('field.all') },
    { id: EDashboardViewMode.onlyVisible, name: this.translate.instant('field.dashboards') },
    { id: EDashboardViewMode.onlySubDashboards, name: this.translate.instant('field.subDashboards') },
    { id: EDashboardViewMode.slideshows, name: this.translate.instant('page.slideshows.title') },
  ];

  public readonly viewModeDropdownControl = new FormControl<IDropdownOption<EDashboardViewMode>[]>(
    this.viewModeOptions.filter((option) => option.id === this.viewModeControl.value),
    { nonNullable: true },
  );

  public ngOnInit(): void {
    this.addEditForm.get('workspace')?.valueChanges.subscribe((value: IDropdownOption[] | null): void => {
      const workspaceId: number | undefined = get(value, '0.id');
      const adminsDropdown = this.addEditForm.get('admins');
      const usersDropdown = this.addEditForm.get('users');

      usersDropdown?.enable();
      adminsDropdown?.enable();

      if (!workspaceId) {
        usersDropdown?.disable();
        adminsDropdown?.disable();
        usersDropdown?.setValue([]);
        adminsDropdown?.setValue([]);
      }
    });
    this.subscriptions.push(
      this.store.afterCrudSuccess$.subscribe(() => {
        this.matDialog.closeAll();
        this.snackbarService.open(this.translate.instant('system.message.changesSuccessfully'), ESnackbar.success);
        this.getItems(this.currentParentId, this.search);
      }),
      combineLatest([this.route.paramMap, this.route.queryParamMap]).subscribe(
        ([params, queryParams]: [ParamMap, ParamMap]) => this.onRouteChanged(params, queryParams),
      ),
      this.viewModeControl.valueChanges.subscribe((mode: EDashboardViewMode) => this.onViewModeChanged(mode)),
      this.viewModeDropdownControl.valueChanges.subscribe((options: IDropdownOption<EDashboardViewMode>[]) => {
        const mode: EDashboardViewMode | undefined = options[0]?.id;

        if (!mode) {
          this.syncViewModeState();

          return;
        }

        if (mode !== this.viewModeControl.value) {
          this.viewModeControl.setValue(mode);
        }
      }),
      this.searchControl.valueChanges
        .pipe(
          debounceTime(600),
          map((value) => value?.trim() ?? ''),
          distinctUntilChanged(),
          filter((value) => value.length === 0 || value.length >= 3),
        )
        .subscribe((value) => {
          const isRefiningSearch: boolean = this.currentParentId === null && this.isAllMode && !!this.search;

          this.router.navigate(DashboardsRoute.list(), {
            queryParams: this.buildRootQueryParams(EDashboardViewMode.all, value || undefined),
            replaceUrl: isRefiningSearch,
          });
        }),
    );
  }

  public initializeAdd(): void {
    this.setupForAdd();
    this.addEditForm.enable();

    this.isAutoLockConfigured = false;
    this.addEditForm.reset({
      autoLockDuration: ScreenLockConfigurationComponent.toDurationSelection(null, this.screenLockDurationOptions),
      layoutMode: this.layoutOptions.filter((option) => option.id === EDashboardLayout.fixedHeight),
      refreshInterval: this.refreshIntervalData.filter((option) => option.id === this.defaultRefreshIntervalId),
      workspace: [],
    });

    this.initializeAddEditDialog();

    this.matDialog.open(this.addEditFormDialog, {
      disableClose: true,
      maxWidth: '100%',
      restoreFocus: false,
      width: EDialogWidth.xSmall,
    });
  }

  public initializeEdit(): void {
    if (!this.selectedRows().length) {
      return;
    }

    this.addEditForm.enable();
    this.setupForEdit();
    const [item] = this.selectedRows();

    const refreshInterval = this.refreshIntervalData.find(({ id }) => id === item.refreshInterval);

    this.isAutoLockConfigured = item.isAutoLockConfigured;
    this.addEditForm.reset({
      admins: item.dashboardUserAssignments
        .filter(({ isAdmin }) => isAdmin)
        .map(({ userId }) => ({ id: userId, name: '...' })),
      autoLockDuration: ScreenLockConfigurationComponent.toDurationSelection(
        item.autoLockDurationSeconds,
        this.screenLockDurationOptions,
      ),
      isHidden: item.isHidden,
      isPublished: item.isPublished,
      isWidgetFilterButtonHidden: item.isWidgetFilterButtonHidden,
      layoutMode: this.layoutOptions.filter((option) => option.id === item.layoutModeId),
      name: item.name,
      parent: item.parent ? [{ id: item.parent.id, name: item.parent.name }] : [],
      refreshInterval: refreshInterval ? [refreshInterval] : null,
      teams: item.dashboardTeamAssignments.map(({ teamId }) => ({ id: teamId, name: '...' })),
      users: item.dashboardUserAssignments
        .filter(({ isAdmin }) => !isAdmin)
        .map(({ userId }) => ({ id: userId, name: '...' })),
      workspace: [{ id: item.workspaceId, name: '...' }],
    });

    this.initializeAddEditDialog(item);

    this.matDialog.open(this.addEditFormDialog, {
      disableClose: true,
      restoreFocus: false,
      width: EDialogWidth.xSmall,
    });
  }

  public onFormDialogSubmit(): void {
    if (!this.addEditForm.valid) {
      this.isExtendedAccessPanel = this.addEditForm.controls.admins.invalid;
      this.isExtendedDisplayConfigurationPanel =
        this.addEditForm.controls.refreshInterval.invalid || this.addEditForm.controls.layoutMode.invalid;
      this.isExtendedScreenLockPanel =
        this.addEditForm.controls.autoLockPin.invalid || this.addEditForm.controls.autoLockDuration.invalid;

      return;
    }

    if (!this.isEditDialog()) {
      this.store.createOne(this.formatOnePayload(this.addEditForm.value as TFormDashboardComponent));
    } else {
      const [item] = this.selectedRows();
      this.store.updateOne({
        dto: this.formatOnePayload(this.addEditForm.value as TFormDashboardComponent),
        id: item.id,
      });
    }
  }

  public onSelectedWorkspace(): void {
    this.addEditForm.get('admins')?.reset();
    this.addEditForm.get('users')?.reset();

    if (!this.isEditDialog()) {
      this.selectCurrentUser();
    }
  }

  public getUsers(search?: string): void {
    const workspaceId: number | undefined = get(this.addEditForm, 'value.workspace.0.id');

    if (!workspaceId) {
      return;
    }

    this.store.loadUserOptions({
      params: {
        fields: ['name'],
        filters: [{ field: 'isActive', ids: [true] }],
        limit: 500,
        ...(search ? { search: { searchedFields: ['name'], searchText: search } } : {}),
      },
      workspaceId,
    });
  }

  public getAdmins(search?: string): void {
    const workspaceId: number | undefined = get(this.addEditForm, 'value.workspace.0.id');

    if (!workspaceId) {
      return;
    }

    this.store.loadAdminOptions({
      params: {
        fields: ['name'],
        filters: [{ field: 'isActive', ids: [true] }],
        limit: 500,
        ...(search ? { search: { searchedFields: ['name'], searchText: search } } : {}),
      },
      workspaceId,
    });
  }

  public getTeams(search?: string): void {
    this.store.loadTeamOptions({
      fields: ['name'],
      filters: [{ field: 'isActive', ids: [true] }],
      ...(search ? { search: { searchedFields: ['name'], searchText: search } } : {}),
    });
  }

  public getWorkspaces(search?: string): void {
    this.store.loadWorkspaces({
      fields: ['name'],
      sort: [{ active: 'name', direction: 'asc' }],
      ...(search ? { search: { searchedFields: ['name'], searchText: search } } : {}),
    });
  }

  public getParentDashboards(search?: string): void {
    const editingDashboardId: number | undefined | null = this.isEditDialog() ? this.selectedRows()[0]?.id : null;

    this.store.loadParents({
      fields: ['name'],
      filters: editingDashboardId ? [{ field: 'id', ids: [editingDashboardId], operator: '$notin' as any }] : [],
      sort: [{ active: 'name', direction: 'asc' }],
      ...(search ? { search: { searchedFields: ['name'], searchText: search } } : {}),
    });
  }

  public onClickedItem(id: number): void {
    this.selectedDatatableItems.set([id]);
  }

  public onSortOptionSelected(option: ECardSortOption): void {
    if (this.sortOption() === option) {
      return;
    }

    this.sortOption.set(option);

    this.router.navigate([], {
      queryParams: { [DASHBOARDS_QUERY_PARAMS.sort]: option === ECardSortOption.name ? null : option },
      queryParamsHandling: 'merge',
      relativeTo: this.route,
      replaceUrl: true,
    });
  }

  public togglePin(item: IDashboardCard): void {
    const isPinned = !item.isPinned;

    this.store.patchOneItem({ changes: { isPinned }, id: item.id });

    this.subscriptions.push(
      this.userPinService.toggle('dashboard', item.id).subscribe({
        error: () => this.store.patchOneItem({ changes: { isPinned: !isPinned }, id: item.id }),
        next: () => this.getItems(this.currentParentId, this.search),
      }),
    );
  }

  public initializeClone(): void {
    if (!this.selectedRows().length) {
      return;
    }

    this.matDialog.open(CloneDialogComponent, {
      data: {
        cancelButtonText: this.translate.instant('button.cancel'),
        submitButtonText: this.translate.instant('button.clone'),
        submitHandler: (trimmedName): Observable<unknown> => {
          this.store.clone({
            id: this.selectedRows()[0].id,
            name: trimmedName,
          });

          return this.store.afterCrudSuccess$;
        },
        title: this.translate.instant('system.clone.confirmationTitle', {
          context: this.translate.instant(this.context.singleContext),
        }),
      } satisfies ICloneDialogConfig,
      disableClose: true,
      restoreFocus: false,
      width: EDialogWidth.xSmall,
    });
  }

  public override initializeDelete(): void {
    const [selectedRow] = this.selectedRows();

    if (!selectedRow) {
      return;
    }

    const content = selectedRow.isInUseAsASubDashboard
      ? this.translate.instant('page.dashboards.isInUseAsASubDashboardWarning')
      : this.translate.instant('system.delete.confirmationSingleContent', {
          context: selectedRow[this.context.nameProperty],
        });

    const dialogRef: MatDialogRef<ConfirmationDialogComponent> = this.matDialog.open(ConfirmationDialogComponent, {
      data: {
        cancelButtonText: this.translate.instant('button.cancel'),
        content,
        submitButtonText: this.translate.instant('button.delete'),
        title: this.translate.instant('system.delete.confirmationTitle', {
          context: this.translate.instant(this.context.singleContext),
        }),
      },
    });

    this.subscriptions.push(
      dialogRef.afterClosed().subscribe((result) => {
        if (result) {
          this.datatableStore.deleteOne(selectedRow.id);
        }
      }),
    );
  }

  private getItems(parentId: number | null = null, search?: string): void {
    if (this.isSlideshowsMode) {
      return;
    }

    this.store.loadItems({
      fields: [
        'name',
        'refreshInterval',
        'workspaceId',
        'isInUseAsASubDashboard',
        'isHidden',
        'isPublished',
        'isWidgetFilterButtonHidden',
        'layoutModeId',
        'createdAt',
        'autoLockDurationSeconds',
        'isAutoLockConfigured',
      ],
      filters: DashboardsComponent.buildListFilters(parentId, this.viewModeControl.value),
      join: [
        'workspace||name',
        'dashboardTeamAssignments||teamId',
        'dashboardUserAssignments||userId,isAdmin',
        'parent||name',
        'children||name,isHidden',
      ],
      limit: 1000,
      page: 1,
      sort: CARD_SORT_REQUEST_SORTS[this.sortOption()],
      ...(search
        ? {
            search: {
              searchedFields: ['name'],
              searchText: search,
            },
          }
        : {}),
    });
  }

  private initializeAddEditDialog(item?: IDashboard): void {
    this.isExtendedAccessPanel = false;
    this.isExtendedDisplayConfigurationPanel = false;
    this.isExtendedScreenLockPanel = false;
    this.getUsers();
    this.getAdmins();
    this.getTeams();
    this.getWorkspaces();

    if (!item) {
      this.store
        .select((state: IDashboardState) => state.workspacesLoading)
        .pipe(
          filter((value: ELoadStatus) => value === ELoadStatus.success),
          take(1),
        )
        .subscribe(() => {
          const workspaces = this.store.workspaces();
          const oneItem: IDropdownOption | undefined = workspaces.length === 1 ? workspaces[0] : undefined;

          if (oneItem) {
            this.selectCurrentUser();
            this.addEditForm.get('workspace')!.setValue([oneItem]);
          }
        });

      return;
    }
  }

  private formatOnePayload(formData: TFormDashboardComponent): TAddDashboard {
    const selectedRefreshInterval = get(formData.refreshInterval, '0.id');

    return {
      ...ScreenLockConfigurationComponent.toPayload(formData.autoLockDuration, formData.autoLockPin),
      adminIds: formData.admins?.length ? formData.admins.map((admin: IDropdownOption) => admin.id) : [],
      isHidden: formData.isHidden,
      isPublished: formData.isPublished,
      isWidgetFilterButtonHidden: formData.isWidgetFilterButtonHidden,
      layoutModeId: get(formData.layoutMode, '0.id'),
      name: FormUtilitiesService.trimIfString(formData.name),
      parentId: formData.parent?.length ? formData.parent[0].id : null,
      refreshInterval: selectedRefreshInterval ?? null,
      teamIds: formData.teams?.length ? formData.teams.map((team: IDropdownOption) => team.id) : [],
      userIds: formData.users?.length ? formData.users.map((user: IDropdownOption) => user.id) : [],
      workspaceId: get(formData.workspace, '0.id'),
    };
  }

  private selectCurrentUser(): void {
    const currentUser: ICurrentUser = UserEffects.getUser();

    this.addEditForm.get('admins')!.setValue([
      {
        id: currentUser.id,
        name: currentUser.name,
      },
    ]);
  }

  public openChildDashboards({ id }: IDashboardCard): void {
    this.router.navigate(DashboardsRoute.children(id), { queryParams: this.sortQueryParams });
  }

  private resetSearchSilently(): void {
    this.search = undefined;
    this.searchControl.setValue('', { emitEvent: false });
  }

  private onRouteChanged(params: ParamMap, queryParams: ParamMap): void {
    const idParam: string | null = params.get('id');
    const parsedId: number | null = idParam !== null ? Number(idParam) : null;

    if (idParam !== null && (parsedId === null || Number.isNaN(parsedId))) {
      this.router.navigate(DashboardsRoute.list());

      return;
    }

    this.currentParentId = parsedId;
    this.syncBreadcrumbManager();

    const sort: ECardSortOption = DashboardsComponent.parseSortOption(queryParams.get(DASHBOARDS_QUERY_PARAMS.sort));

    if (this.sortOption() !== sort) {
      this.sortOption.set(sort);
    }

    if (this.currentParentId !== null) {
      this.viewModeControl.setValue(EDashboardViewMode.onlyVisible, { emitEvent: false });
      this.syncViewModeState();
      this.resetSearchSilently();

      this.store
        .getAncestorChain(this.currentParentId)
        .pipe(take(1))
        .subscribe((nodes: IBreadcrumbNode[]) => {
          this.breadcrumbNodes = nodes;
          this.headerBreadcrumbManager?.setNodes(nodes);
        });
    } else {
      const view: EDashboardViewMode = DashboardsComponent.parseViewMode(queryParams.get(DASHBOARDS_QUERY_PARAMS.view));
      const search: string | undefined = queryParams.get(DASHBOARDS_QUERY_PARAMS.search) ?? undefined;

      if (this.viewModeControl.value !== view) {
        this.viewModeControl.setValue(view, { emitEvent: false });
        this.syncViewModeState();
      }

      if ((this.searchControl.value ?? '') !== (search ?? '')) {
        this.searchControl.setValue(search ?? '', { emitEvent: false });
      }

      this.search = search;
      this.breadcrumbNodes = [];
      this.headerBreadcrumbManager?.setNodes([]);
    }

    this.getItems(this.currentParentId, this.search);
  }

  public get isSlideshowsMode(): boolean {
    return this.viewModeControl.value === EDashboardViewMode.slideshows;
  }

  public get isAllMode(): boolean {
    return this.viewModeControl.value === EDashboardViewMode.all;
  }

  public onAddButtonClicked(): void {
    if (this.isSlideshowsMode) {
      this.slideshowsComponent?.initializeAdd();

      return;
    }

    this.initializeAdd();
  }

  private syncViewModeState(): void {
    this.viewMode.set(this.viewModeControl.value);
    this.viewModeDropdownControl.setValue(
      this.viewModeOptions.filter((option) => option.id === this.viewModeControl.value),
      { emitEvent: false },
    );
  }

  private onViewModeChanged(mode: EDashboardViewMode): void {
    const hadSearch = !!this.searchControl.value;

    this.syncViewModeState();

    this.resetSearchSilently();

    if (hadSearch && (mode === EDashboardViewMode.slideshows || mode === EDashboardViewMode.all)) {
      this.slideshowsComponent?.resetSearch();
    }

    if (
      mode === EDashboardViewMode.all ||
      mode === EDashboardViewMode.onlySubDashboards ||
      mode === EDashboardViewMode.slideshows
    ) {
      this.router.navigate(DashboardsRoute.list(), { queryParams: this.buildRootQueryParams(mode) });

      return;
    }

    if (this.currentParentId !== null) {
      this.getItems(this.currentParentId, this.search);

      return;
    }

    this.router.navigate(DashboardsRoute.list(), { queryParams: this.sortQueryParams });
  }

  public static parseViewMode(value: string | null): EDashboardViewMode {
    // Legacy query-param values kept alive so old bookmarks and shared links keep working.
    if (value === 'both') {
      return EDashboardViewMode.all;
    }

    if (value === 'onlyHidden') {
      return EDashboardViewMode.onlySubDashboards;
    }

    return value === EDashboardViewMode.all ||
      value === EDashboardViewMode.onlySubDashboards ||
      value === EDashboardViewMode.slideshows
      ? value
      : EDashboardViewMode.onlyVisible;
  }

  public static parseSortOption(value: string | null): ECardSortOption {
    return value === ECardSortOption.createdAt || value === ECardSortOption.workspace ? value : ECardSortOption.name;
  }

  private get sortQueryParams(): Params {
    return this.sortOption() === ECardSortOption.name ? {} : { [DASHBOARDS_QUERY_PARAMS.sort]: this.sortOption() };
  }

  private buildRootQueryParams(view: EDashboardViewMode, search?: string): Params {
    const params: Params = { [DASHBOARDS_QUERY_PARAMS.view]: view };

    if (search) {
      params[DASHBOARDS_QUERY_PARAMS.search] = search;
    }

    if (this.sortOption() !== ECardSortOption.name) {
      params[DASHBOARDS_QUERY_PARAMS.sort] = this.sortOption();
    }

    return params;
  }

  private syncBreadcrumbManager(): void {
    const manager: HeaderBreadcrumbManager = this.route.snapshot.data['headerBreadcrumbManager'];

    if (manager === this.headerBreadcrumbManager) {
      return;
    }

    this.headerBreadcrumbManager = manager;
    this.nodeClickedSubscription?.unsubscribe();

    this.nodeClickedSubscription = manager?.nodeClicked$.subscribe((node?: IBreadcrumbNode) => {
      this.router.navigate(node?.id !== undefined ? DashboardsRoute.children(node.id) : DashboardsRoute.list(), {
        queryParams: this.sortQueryParams,
      });
    });

    if (this.nodeClickedSubscription) {
      this.subscriptions.push(this.nodeClickedSubscription);
    }
  }
}
